import React, { useState, useEffect } from 'react';
import './PaymentModal.css';

// Lazy load Stripe components to avoid compilation errors
let Elements, CardElement, useStripe, useElements;
let StripeService;

try {
  const stripeReact = require('@stripe/react-stripe-js');
  Elements = stripeReact.Elements;
  CardElement = stripeReact.CardElement;
  useStripe = stripeReact.useStripe;
  useElements = stripeReact.useElements;
  StripeService = require('services/stripe').default;
} catch (error) {
  console.warn('Stripe dependencies not installed yet. Please run: npm install @stripe/stripe-js @stripe/react-stripe-js');
}

// Card element styling to match your design
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: false,
};

// Payment form component
const PaymentForm = ({ onSuccess, onCancel, planDetails }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    address: {
      line1: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    },
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Create payment intent
      const paymentIntentData = {
        amount: planDetails.amount * 100, // Convert to cents
        currency: 'usd',
        plan: planDetails.plan,
        billing_details: billingDetails,
      };

      const { client_secret } = await StripeService.createPaymentIntent(paymentIntentData);

      // Process payment
      const result = await StripeService.processPayment(
        stripe,
        elements,
        client_secret,
        billingDetails
      );

      if (result.success) {
        onSuccess(result.paymentIntent);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBillingDetails(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setBillingDetails(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="form-section">
        <h3>Billing Information</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              value={billingDetails.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              placeholder="John Doe"
            />
          </div>
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={billingDetails.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              placeholder="john@example.com"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Address</label>
          <input
            type="text"
            value={billingDetails.address.line1}
            onChange={(e) => handleInputChange('address.line1', e.target.value)}
            required
            placeholder="123 Main Street"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>City</label>
            <input
              type="text"
              value={billingDetails.address.city}
              onChange={(e) => handleInputChange('address.city', e.target.value)}
              required
              placeholder="San Francisco"
            />
          </div>
          
          <div className="form-group">
            <label>State</label>
            <input
              type="text"
              value={billingDetails.address.state}
              onChange={(e) => handleInputChange('address.state', e.target.value)}
              required
              placeholder="CA"
            />
          </div>
          
          <div className="form-group">
            <label>ZIP Code</label>
            <input
              type="text"
              value={billingDetails.address.postal_code}
              onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
              required
              placeholder="94102"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Payment Information</h3>
        
        <div className="form-group">
          <label>Card Details</label>
          <div className="card-element-container">
            <CardElement options={cardElementOptions} />
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={isProcessing}
        >
          Cancel
        </button>
        
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!stripe || isProcessing}
        >
          {isProcessing ? 'Processing...' : `Pay $${planDetails.amount}`}
        </button>
      </div>
    </form>
  );
};

// Main payment modal component
const PaymentModal = ({ isOpen, onClose, onSuccess, planDetails }) => {
  const [stripeInstance, setStripeInstance] = useState(undefined);

  useEffect(() => {
    if (!isOpen || !StripeService) return;
    let cancelled = false;
    StripeService.getStripe().then((stripe) => {
      if (!cancelled) setStripeInstance(stripe);
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSuccess = (paymentIntent) => {
    onSuccess(paymentIntent);
    onClose();
  };

  if (!Elements || !StripeService) {
    return (
      <div className="payment-modal-overlay">
        <div className="payment-modal">
          <div className="modal-header">
            <h2>Payment Setup Required</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="modal-content">
            <div className="setup-message">
              <h3>Stripe Dependencies Missing</h3>
              <p>To enable payments, please install the required dependencies:</p>
              <code>npm install @stripe/stripe-js @stripe/react-stripe-js</code>
              <p>Then add your Stripe keys to the environment variables.</p>
              <button className="btn btn-primary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stripeInstance === null) {
    return (
      <div className="payment-modal-overlay">
        <div className="payment-modal">
          <div className="modal-header">
            <h2>Payment</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="modal-content">
            <div className="setup-message">
              <h3>Stripe couldn&apos;t be loaded</h3>
              <p>Payment script failed to load (check network or try again later).</p>
              <button className="btn btn-primary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stripeInstance === undefined) {
    return (
      <div className="payment-modal-overlay">
        <div className="payment-modal">
          <div className="modal-header">
            <h2>Complete Your Payment</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="modal-content">
            <p className="setup-message">Loading payment form...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-modal-overlay">
      <div className="payment-modal">
        <div className="modal-header">
          <h2>Complete Your Payment</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-content">
          <div className="plan-summary">
            <h3>{planDetails.plan}</h3>
            <p className="plan-price">${planDetails.amount}/month</p>
            <p className="plan-description">
              Unlimited integrations, advanced AI features, and priority support
            </p>
          </div>
          <Elements stripe={stripeInstance}>
            <PaymentForm
              onSuccess={handleSuccess}
              onCancel={onClose}
              planDetails={planDetails}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
